/**
 * Frise d'une année scolaire (packs de SKILLCARDS, migration 0289) — partagée
 * app ⇄ site. Mobile : frise VERTICALE ; à partir de `md` : frise HORIZONTALE
 * défilante (`orientation="vertical"` : verticale partout). Le pack en cours (`currentIndex`) est mis en avant ; avec
 * `progress`, chaque pack affiche le % de cartes maîtrisées (élève).
 */

import type { ReactNode } from 'react';
import {
  Box,
  Calculator,
  ChartArea,
  ChartColumn,
  ChartLine,
  Code,
  Compass,
  Dices,
  GraduationCap,
  Hash,
  Infinity as InfinityIcon,
  Percent,
  Scale,
  Sigma,
  Sparkles,
  SquareFunction,
  Superscript,
  TrendingUp,
  Triangle,
  type LucideIcon,
} from 'lucide-react';
import { packIcon, packPeriodLabel, type PackIcon } from '@jeraisonne/db/curriculum';
import { cn } from '../lib/utils';

const ICONS: Record<PackIcon, LucideIcon> = {
  sparkles: Sparkles,
  calculator: Calculator,
  hash: Hash,
  superscript: Superscript,
  sigma: Sigma,
  scale: Scale,
  triangle: Triangle,
  'line-chart': ChartLine,
  percent: Percent,
  compass: Compass,
  dices: Dices,
  box: Box,
  code: Code,
  'graduation-cap': GraduationCap,
  infinity: InfinityIcon,
  'trending-up': TrendingUp,
  'function-square': SquareFunction,
  'area-chart': ChartArea,
  'bar-chart': ChartColumn,
};

export function PackIconGlyph({
  icon,
  className,
}: {
  icon: string | null | undefined;
  className?: string;
}) {
  const Icon = ICONS[packIcon(icon)];
  return <Icon className={className} aria-hidden />;
}

export interface TimelinePack {
  id: string;
  title: string;
  hook?: string;
  startMonth: number;
  /** Fin INDICATIVE dans l'année (planning) ; absente = un seul mois. */
  endMonth?: number | null;
  color?: string | null;
  icon?: string | null;
  cardCount: number;
  /** % de cartes maîtrisées (0..100) ; null = pack sans carte ; absent = pas de progression. */
  progress?: number | null;
  hora?: number;
  href?: string;
}

export interface CurriculumTimelineProps {
  packs: TimelinePack[];
  currentIndex?: number;
  /** Lien (Link du routeur, <a>…) ; sans renderLink ni href, la carte n'est pas cliquable. */
  renderLink?: (pack: TimelinePack, children: ReactNode, className: string) => ReactNode;
  className?: string;
  label?: string;
  /** `responsive` (défaut) : horizontale dès `md` ; `vertical` : une ligne par pack partout. */
  orientation?: 'responsive' | 'vertical';
}

const DEFAULT_COLOR = '#4DA3FF';

export type TimelinePackPhase = 'past' | 'current' | 'upcoming';

/**
 * Place d'un pack dans l'année : avant le pack en cours = passé, après = à
 * venir. Sans pack en cours connu (`currentIndex < 0`), tous sont « passés »
 * (carte pleine, affichage historique).
 */
export function timelinePackPhase(index: number, currentIndex: number): TimelinePackPhase {
  if (currentIndex < 0) return 'past';
  if (index === currentIndex) return 'current';
  return index < currentIndex ? 'past' : 'upcoming';
}

export function CurriculumTimeline({
  packs,
  currentIndex = -1,
  renderLink,
  className,
  label = 'Progression de l’année',
  orientation = 'responsive',
}: CurriculumTimelineProps) {
  const row = orientation === 'responsive';
  return (
    <ol
      aria-label={label}
      className={cn(
        'relative space-y-4 pl-6',
        row &&
          'md:-mx-1 md:flex md:snap-x md:snap-mandatory md:gap-4 md:space-y-0 md:overflow-x-auto md:px-1 md:pb-3 md:pl-1 md:pt-6',
        className,
      )}
    >
      {/* Le fil de la frise : vertical sur mobile, horizontal sur desktop. */}
      <span
        aria-hidden
        className={cn(
          'bg-ink-200 absolute bottom-2 left-[0.6875rem] top-2 w-0.5',
          row && 'md:left-1 md:right-1 md:top-[1.4rem] md:h-0.5 md:w-auto',
        )}
      />
      {packs.map((pack, i) => {
        const color = pack.color ?? DEFAULT_COLOR;
        const current = i === currentIndex;
        const phase = timelinePackPhase(i, currentIndex);
        const cardBody = (
          <>
            <div className="flex items-center gap-2">
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                style={{ background: `${color}22`, color }}
              >
                <PackIconGlyph icon={pack.icon} className="h-4 w-4" />
              </span>
              <span className="text-ink-500 text-xs font-semibold uppercase tracking-wide">
                {packPeriodLabel(pack.startMonth, pack.endMonth)}
              </span>
              {current && (
                <span className="bg-primary-600 ml-auto rounded-full px-2 py-0.5 text-xs font-bold text-white">
                  En ce moment
                </span>
              )}
              {phase === 'upcoming' && (
                <span className="text-ink-500 ml-auto text-xs font-semibold">À venir</span>
              )}
            </div>
            <p className="text-ink-900 font-display mt-2 text-sm font-bold leading-snug">
              {pack.title}
            </p>
            {pack.hook && <p className="text-ink-600 mt-1 text-xs leading-snug">{pack.hook}</p>}
            <p className="text-ink-500 mt-2 text-xs">
              {pack.cardCount > 0
                ? `${pack.cardCount} SKILLCARD${pack.cardCount > 1 ? 'S' : ''}`
                : 'SKILLCARDS bientôt disponibles'}
            </p>
            {pack.progress !== undefined && pack.cardCount > 0 && (
              <div className="mt-2">
                <div
                  className="bg-ink-100 h-1.5 overflow-hidden rounded-full"
                  role="progressbar"
                  aria-label={`${pack.title} : cartes maîtrisées`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={pack.progress ?? 0}
                >
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${pack.progress ?? 0}%`, background: color }}
                  />
                </div>
                <p className="text-ink-600 mt-1 text-[11px]">
                  {pack.progress ?? 0} % maîtrisé{pack.hora ? ` · ${pack.hora} HORA` : ''}
                </p>
              </div>
            )}
          </>
        );
        // Passé = carte pleine ; en cours = teintée et cerclée ; à venir = fond
        // transparent, bord pointillé.
        const cardClass = cn(
          'block rounded-2xl border p-3 transition',
          phase === 'current' &&
            'border-primary-400 bg-primary-50 ocean:bg-primary-500/20 ring-primary-200 ocean:ring-primary-500/30 shadow-md ring-1',
          phase === 'past' && 'border-ink-200 bg-white',
          phase === 'upcoming' && 'border-ink-200 border-dashed bg-transparent',
          (renderLink || pack.href) && 'hover:border-primary-300 active:scale-[0.99]',
        );
        return (
          <li
            key={pack.id}
            data-phase={phase}
            className={cn('relative', row && 'md:w-60 md:shrink-0 md:snap-start')}
          >
            <span
              aria-hidden
              className={cn(
                'absolute -left-6 top-4 h-3.5 w-3.5 rounded-full border-2',
                row && 'md:-top-[1.15rem] md:left-3',
                phase === 'upcoming' ? 'bg-transparent' : 'border-white',
                current && 'ring-primary-300 ring-4',
              )}
              style={phase === 'upcoming' ? { borderColor: color } : { background: color }}
            />
            {renderLink ? (
              renderLink(pack, cardBody, cardClass)
            ) : pack.href ? (
              <a href={pack.href} className={cardClass}>
                {cardBody}
              </a>
            ) : (
              <div className={cardClass}>{cardBody}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}
